import { z } from 'zod';
import { boardParams, endpoint, isoDate, isTimeZone, LIMITS } from './common';

// How a board is doing over a period.

export const boardStats = z.object({
    range: z.object({ from: z.string(), to: z.string() }),
    // Tasks completed in the range (a task completed and then un-completed doesn't count), and the same count for the
    // equally long period before it.
    completed: z.object({ current: z.number(), previous: z.number() }),
    // Tasks whose due time fell inside the range, and how many of them are done now.
    due: z.object({ total: z.number(), completed: z.number() }),
    // Of the completions in the range that had a due time, how many were at or before it.
    onTime: z.object({ onTime: z.number(), total: z.number() }),
    overdueNow: z.number(),
    perDay: z.array(z.object({ day: z.string(), count: z.number() })),
    sections: z.array(z.object({ sectionId: z.string(), total: z.number(), completed: z.number() })),
    series: z.array(z.object({ seriesId: z.string(), title: z.string(), total: z.number(), completed: z.number(), streak: z.number() })),
    // Per-person completions; only present for owners and editors.
    people: z.array(z.object({ userId: z.string().nullable(), name: z.string(), count: z.number() })).optional(),
});
export type BoardStats = z.infer<typeof boardStats>;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const getBoardStats = endpoint({
    method: 'GET',
    path: '/boards/:boardId/stats',
    params: boardParams,
    query: z.object({
        from: isoDate,
        to: isoDate,
        // Days are bucketed in this IANA time zone.
        tz: z.string().default('UTC').refine(isTimeZone, 'tz must be a valid IANA time zone'),
    }).superRefine((query, context) => {
        const from = new Date(query.from).getTime();
        const to = new Date(query.to).getTime();
        if (!(from < to)) {
            context.addIssue({ code: 'custom', path: ['from'], message: 'from and to must be valid dates, with from before to' });
        } else if (to - from > LIMITS.statsRangeDays * MS_PER_DAY) {
            context.addIssue({ code: 'custom', path: ['to'], message: `the range can be at most ${LIMITS.statsRangeDays} days` });
        }
    }),
    response: z.object({ stats: boardStats }),
});
